/**
 * Gross/net conversion for the live figures the Creator shows while the
 * seller types. The server (`apps/api/src/pricing/quote.ts`) calculates net
 * and converts the gross selling price once with the quote's `vatRate`; the
 * helpers here repeat that single step with the same cent rounding so the
 * number under the price field matches the number the next save books.
 *
 * Money is only ever derived here for display — nothing computed on the
 * client is stored or sent.
 */

/**
 * The rate the server always uses (decision 2026-09-23: 19 %, no
 * configuration). Stands in when a quote carries no `vatRate` — an API build
 * from before the net calculation during a deploy window — so the live
 * figure is net either way.
 */
export const DEFAULT_VAT_RATE = 19;

/** Rounds to cents, half away from zero, like the server's `toCents`. */
export function round2(value: number): number {
  return (Math.sign(value) * Math.round(Math.abs(value) * 100)) / 100;
}

/** A cent amount (possibly fractional) rounded half away from zero, in euros. */
function round2Cents(cents: number): number {
  return (Math.sign(cents) * Math.round(Math.abs(cents))) / 100;
}

function parseAmount(value: number | string): number | null {
  // Number('') is 0, which would price an empty field; treat it as absent.
  if (typeof value === 'string' && value.trim() === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/** The quote's rate, or DEFAULT_VAT_RATE when it is missing or unparseable. */
export function vatRateOf(vatRate: string | number | null | undefined): number {
  if (vatRate === undefined || vatRate === null || vatRate === '') {
    return DEFAULT_VAT_RATE;
  }
  const rate = Number(vatRate);
  return Number.isFinite(rate) && rate >= 0 ? rate : DEFAULT_VAT_RATE;
}

/**
 * Gross to net, rounded to cents; null when the amount is missing or
 * unparseable. Works on whole cents so float drift cannot flip a rounding.
 */
export function netOf(
  gross: number | string,
  vatRate: string | number | null | undefined
): number | null {
  const amount = parseAmount(gross);
  if (amount === null) return null;
  const cents = Math.round(amount * 100);
  return round2Cents((cents * 100) / (100 + vatRateOf(vatRate)));
}

/**
 * Net to gross, rounded to cents (half up, like the server); null when the
 * amount is missing or unparseable. For a seller cost this is the minimum
 * selling price the server's floor accepts.
 */
export function grossOf(
  net: number | string,
  vatRate: string | number | null | undefined
): number | null {
  const amount = parseAmount(net);
  if (amount === null) return null;
  const cents = Math.round(amount * 100);
  // cents × (100 + rate) / 100 is exact for whole-number rates, so the
  // half-cent case is exact and rounds up like `toCents`.
  return round2Cents((cents * (100 + vatRateOf(vatRate))) / 100);
}
